// utils/translationService.js - ReactJS Web Version
class TranslationService {
  constructor() {
    this.cache = new Map();
    // Use environment variable for API key (recommended for production)
    this.apiKey = process.env.REACT_APP_GOOGLE_TRANSLATE_API_KEY || "AIzaSyA-xEnlB_GTuCB3dhhqlSEQA7TaGFRrZAU";
    this.apiUrl = 'https://translation.googleapis.com/language/translate/v2';
    this.debug = true;
    this.cacheExpiry = 7 * 24 * 60 * 60 * 1000; // 7 days cache expiry
    this.loadCacheFromStorage();
  }

  log(message, data = null) {
    if (this.debug) {
      console.log(`[TranslationService] ${message}`, data ? data : '');
    }
  }

  async loadCacheFromStorage() {
    try {
      const cached = localStorage.getItem('translation_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        const now = Date.now();
        // Only load non-expired cache entries
        for (const [key, value] of Object.entries(parsed)) {
          if (value.expiry > now) {
            this.cache.set(key, value.text);
          }
        }
        this.log(`Loaded ${this.cache.size} cached translations from storage`);
      }
    } catch (error) {
      this.log(`Error loading cache from storage: ${error.message}`);
    }
  }

  async saveCacheToStorage() {
    try {
      const cacheObject = {};
      const now = Date.now();
      for (const [key, value] of this.cache.entries()) {
        cacheObject[key] = {
          text: value,
          expiry: now + this.cacheExpiry
        };
      }
      localStorage.setItem('translation_cache', JSON.stringify(cacheObject));
      this.log(`Saved ${this.cache.size} translations to storage`);
    } catch (error) {
      this.log(`Error saving cache to storage: ${error.message}`);
    }
  }

  async translateText(text, targetLanguage) {
    this.log(`translateText called:`, { 
      text: text?.substring(0, 50), 
      targetLanguage,
      textLength: text?.length 
    });
    
    if (!text || typeof text !== 'string') {
      this.log('Invalid text input, returning original');
      return text;
    }
    
    if (targetLanguage === 'en') {
      this.log('Returning original text (English)');
      return text;
    }
    
    // Clean text for cache key (remove extra spaces, trim)
    const cleanText = text.trim().replace(/\s+/g, ' ');
    const cacheKey = `${cleanText}_${targetLanguage}`;
    
    // Check memory cache
    if (this.cache.has(cacheKey)) {
      this.log(`Returning cached translation from memory`);
      return this.cache.get(cacheKey);
    }

    try {
      // Check if API key is available
      if (!this.apiKey) {
        this.log('⚠️ No valid API key provided. Please set REACT_APP_GOOGLE_TRANSLATE_API_KEY in your .env file');
        return text;
      }
      
      this.log(`Making API request...`);
      
      const url = `${this.apiUrl}?key=${this.apiKey}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          q: cleanText,
          target: targetLanguage,
          format: 'text'
        })
      });

      this.log(`Response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        this.log(`HTTP Error: ${response.status} - ${errorText}`);
        return text;
      }
      
      const data = await response.json();
      
      if (data.error) {
        this.log(`API Error: ${data.error.message}`);
        console.error('Translation API Error:', data.error);
        return text;
      }

      const translatedText = data.data?.translations?.[0]?.translatedText || text;
      this.log(`Translation successful: "${cleanText.substring(0, 30)}" -> "${translatedText.substring(0, 30)}"`);
      
      // Cache the result in memory
      this.cache.set(cacheKey, translatedText);
      
      // Save to persistent storage asynchronously
      this.saveCacheToStorage().catch(err => this.log(`Cache save error: ${err.message}`));
      
      return translatedText;
    } catch (error) {
      this.log(`Fetch error: ${error.message}`);
      console.error('Translation fetch error:', error);
      return text;
    }
  }

  async translateWithRetry(text, targetLanguage, maxRetries = 3) {
    for (let i = 0; i < maxRetries; i++) {
      const result = await this.translateText(text, targetLanguage);
      if (result !== text || text === result) {
        return result;
      }
      this.log(`Retry ${i + 1}/${maxRetries} for translation`);
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1))); // Exponential backoff
    }
    return text;
  }

  async batchTranslateTexts(texts, targetLanguage) {
    this.log(`batchTranslateTexts called with ${texts?.length || 0} texts`);
    
    if (!texts || texts.length === 0 || targetLanguage === 'en') {
      return texts;
    }
    
    // Filter out invalid texts
    const validTexts = texts.filter(text => text && typeof text === 'string');
    
    // Get unique texts to reduce API calls
    const uniqueTexts = [...new Set(validTexts)];
    const translations = {};
    
    // Process in batches to avoid overwhelming the API
    const batchSize = 5;
    for (let i = 0; i < uniqueTexts.length; i += batchSize) {
      const batch = uniqueTexts.slice(i, i + batchSize);
      const promises = batch.map(text => this.translateText(text, targetLanguage));
      const results = await Promise.all(promises);
      
      batch.forEach((text, index) => {
        translations[text] = results[index];
      });
      
      // Small delay between batches
      if (i + batchSize < uniqueTexts.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    return texts.map(text => translations[text] || text);
  }

  async adaptiveTranslate(text, targetLanguage, context = '') {
    if (!text || targetLanguage === 'en') return text;
    
    // Add context to improve translation accuracy
    const contextualText = context ? `[Context: ${context}] ${text}` : text;
    return await this.translateText(contextualText, targetLanguage);
  }

  async translateMessage(message, targetLanguage, preserveEmojis = true) {
    if (!message || targetLanguage === 'en') return message;
    
    let textToTranslate = message;
    
    // Extract and preserve emojis if needed
    let emojis = [];
    if (preserveEmojis) {
      const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}]/gu;
      textToTranslate = message.replace(emojiRegex, (match) => {
        emojis.push(match);
        return `[EMOJI_${emojis.length - 1}]`;
      });
    }
    
    // Translate the text
    let translated = await this.translateText(textToTranslate, targetLanguage);
    
    // Restore emojis
    if (preserveEmojis) {
      translated = translated.replace(/\[EMOJI_(\d+)\]/g, (match, index) => {
        return emojis[parseInt(index)] || '';
      });
    }
    
    return translated;
  }

  async detectLanguage(text) {
    if (!text) return 'en';
    
    try {
      const url = `https://translation.googleapis.com/language/translate/v2/detect?key=${this.apiKey}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          q: text,
        })
      });
      
      const data = await response.json();
      if (data.data && data.data.detections && data.data.detections[0]) {
        return data.data.detections[0][0].language;
      }
      return 'en';
    } catch (error) {
      this.log(`Language detection error: ${error.message}`);
      return 'en';
    }
  }

  async translateIfNeeded(text, userLanguage, originalLanguage = 'en') {
    if (userLanguage === originalLanguage) {
      return text;
    }
    
    return await this.translateText(text, userLanguage);
  }

  clearCache() {
    const size = this.cache.size;
    this.cache.clear();
    this.log(`Memory cache cleared. Removed ${size} entries.`);
    
    // Also clear persistent storage
    localStorage.removeItem('translation_cache');
    this.log('Persistent cache cleared');
  }

  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      expiryDays: this.cacheExpiry / (24 * 60 * 60 * 1000)
    };
  }

  // Web-specific: Clear cache on page unload
  setupAutoCacheClear() {
    window.addEventListener('beforeunload', () => {
      this.saveCacheToStorage();
    });
  }
}

// Create and export a singleton instance
const translationServiceInstance = new TranslationService();

// Setup auto-save on page unload
translationServiceInstance.setupAutoCacheClear();

export default translationServiceInstance;